import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { api, type ReviewResponse, type ReviewMeta } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { useSubscription } from '@/hooks/useSubscription';
import { BookOpen, Loader2, ArrowLeft, RefreshCw, ChevronRight, ChevronDown, ChevronUp, Lock, Search, FileText, Trash2 } from 'lucide-react';
import PremiumModal from '@/components/PremiumModal';
import { format, parseISO, subWeeks, subMonths, subYears } from 'date-fns';
import { ja } from 'date-fns/locale';

const ReviewAlbum = lazy(() => import('@/components/review/ReviewAlbum'));

type Period = '1week' | '2weeks' | '1month' | '3months' | '6months' | '1year' | 'custom';

interface PeriodOption {
  key: Period;
  label: string;
  premium: boolean;
}

const periodOptions: PeriodOption[] = [
  { key: '1week', label: '1週間', premium: false },
  { key: '2weeks', label: '2週間', premium: false },
  { key: '1month', label: '1ヶ月', premium: false },
  { key: '3months', label: '3ヶ月', premium: true },
  { key: '6months', label: '6ヶ月', premium: true },
  { key: '1year', label: '1年', premium: true },
  { key: 'custom', label: 'カスタム期間', premium: true },
];

const periodLabels: Record<Period, string> = {
  '1week': '1週間',
  '2weeks': '2週間',
  '1month': '1ヶ月',
  '3months': '3ヶ月',
  '6months': '6ヶ月',
  '1year': '1年',
  'custom': 'カスタム期間',
};

function getDateRange(period: Period, customFrom?: string, customTo?: string): { from: string; to: string } {
  const now = new Date();
  const to = format(now, 'yyyy-MM-dd');

  switch (period) {
    case '1week':
      return { from: format(subWeeks(now, 1), 'yyyy-MM-dd'), to };
    case '2weeks':
      return { from: format(subWeeks(now, 2), 'yyyy-MM-dd'), to };
    case '1month':
      return { from: format(subMonths(now, 1), 'yyyy-MM-dd'), to };
    case '3months':
      return { from: format(subMonths(now, 3), 'yyyy-MM-dd'), to };
    case '6months':
      return { from: format(subMonths(now, 6), 'yyyy-MM-dd'), to };
    case '1year':
      return { from: format(subYears(now, 1), 'yyyy-MM-dd'), to };
    case 'custom':
      return { from: customFrom || to, to: customTo || to };
    default:
      return { from: format(subWeeks(now, 1), 'yyyy-MM-dd'), to };
  }
}

export default function ReviewPage() {
  const [period, setPeriod] = useState<Period | null>(null);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [entryCount, setEntryCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [review, setReview] = useState<ReviewResponse | null>(null);
  const [pastReviews, setPastReviews] = useState<ReviewMeta[]>([]);
  const [selectedPast, setSelectedPast] = useState<ReviewResponse | null>(null);
  const [loadingPast, setLoadingPast] = useState(false);
  const [error, setError] = useState('');
  const [formExpanded, setFormExpanded] = useState(true);

  // Swipe delete state
  const [swipedId, setSwipedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const { isPremium, refresh: refreshSubscription } = useSubscription();
  const { toast } = useToast();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  useEffect(() => {
    api.getReviews().then(setPastReviews).catch(() => {});
  }, []);

  // Reset swiped state on outside tap
  useEffect(() => {
    if (!swipedId) return;
    const handler = () => setSwipedId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [swipedId]);

  // Count entries when period is selected (or custom dates change)
  useEffect(() => {
    if (!period) {
      setEntryCount(null);
      return;
    }

    if (period === 'custom' && (!customFrom || !customTo)) {
      setEntryCount(null);
      return;
    }

    let cancelled = false;
    const fetchCount = async () => {
      setCounting(true);
      setEntryCount(null);
      try {
        const { from, to } = getDateRange(period, customFrom, customTo);
        const countResult = await api.searchEntries({ from, to, limit: 1000 });
        if (!cancelled) {
          setEntryCount(countResult.entries.length);
        }
      } catch {
        if (!cancelled) {
          setEntryCount(0);
        }
      } finally {
        if (!cancelled) {
          setCounting(false);
        }
      }
    };

    fetchCount();
    return () => { cancelled = true; };
  }, [period, customFrom, customTo]);

  const handleSelectPeriod = (option: PeriodOption) => {
    if (option.premium && !isPremium) {
      setShowPremiumModal(true);
      return;
    }
    // Reset state when switching period
    setReview(null);
    setSelectedPast(null);
    setError('');
    setEntryCount(null);
    setPeriod(option.key);

    // Reset custom dates when switching away from custom
    if (option.key !== 'custom') {
      setCustomFrom('');
      setCustomTo('');
    }
  };

  const handleGenerate = async () => {
    if (!period) return;

    setGenerating(true);
    setError('');
    setReview(null);
    setSelectedPast(null);
    try {
      const result = await api.generateReview(period as any, customFrom || undefined, customTo || undefined);
      if (result) {
        setReview(result);
        setFormExpanded(false);
        // Refresh past reviews list
        api.getReviews().then(setPastReviews).catch(() => {});
      } else {
        setError('振り返りの生成に失敗しました');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '振り返りの生成に失敗しました');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewPast = async (id: string) => {
    setLoadingPast(true);
    try {
      const r = await api.getReview(id);
      setSelectedPast(r);
      setReview(null);
      setFormExpanded(false);
    } catch {
      setError('振り返りの取得に失敗しました');
    } finally {
      setLoadingPast(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteReview(deleteTarget);
      setPastReviews(prev => prev.filter(r => r.id !== deleteTarget));
      if (selectedPast?.id === deleteTarget) {
        setSelectedPast(null);
      }
      toast({ title: '削除しました' });
    } catch {
      toast({ title: '削除に失敗しました', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
      setSwipedId(null);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (id: string) => (e: React.TouchEvent) => {
    const dx = touchStartX.current - e.touches[0].clientX;
    const dy = Math.abs(touchStartY.current - e.touches[0].clientY);
    // 横方向のスワイプが縦より大きい場合のみ処理
    if (Math.abs(dx) < dy) return;
    if (dx > 60) {
      setSwipedId(id);
    } else if (dx < -30) {
      setSwipedId(null);
    }
  };

  const displayReview = review || selectedPast;
  const canGenerate = period !== null && entryCount !== null && entryCount > 0 && !counting && !generating;

  // コンパクト表示用のラベル
  const compactPeriodLabel = period
    ? periodLabels[period]
    : displayReview
      ? periodLabels[displayReview.period as Period] || displayReview.period
      : '';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">振り返り</h1>
      </div>

      {/* Period selector + Generate button */}
      {!formExpanded && displayReview ? (
        /* Compact form */
        <Card className="rounded-xl shadow-sm">
          <CardContent className="py-3">
            <button
              className="w-full flex items-center justify-between"
              onClick={() => setFormExpanded(true)}
            >
              <div className="flex items-center gap-2 text-sm">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{compactPeriodLabel}</span>
                <span className="text-muted-foreground">
                  {displayReview.entryCount}件
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <span>変更</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </div>
            </button>
          </CardContent>
        </Card>
      ) : (
        /* Full form */
        <Card className="rounded-xl shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="rounded-full p-1.5 bg-purple-500/10">
                  <BookOpen className="h-5 w-5 text-purple-500" />
                </div>
                振り返りを生成
              </CardTitle>
              {displayReview && (
                <Button variant="ghost" size="sm" onClick={() => setFormExpanded(false)}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
              )}
            </div>
            <CardDescription>AIが期間内のエントリを分析し、振り返りをまとめます</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Step 1: Period selection — card style */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {periodOptions.map((option) => (
                <button
                  key={option.key}
                  className={`relative flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                    period === option.key
                      ? 'border-primary bg-primary/5 text-primary shadow-sm'
                      : 'border-border bg-card hover:border-primary/30 hover:shadow-sm'
                  }`}
                  onClick={() => handleSelectPeriod(option)}
                >
                  {option.premium && !isPremium && (
                    <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />
                  )}
                  <span className="truncate">{option.label}</span>
                </button>
              ))}
            </div>

            {/* Custom date range (only shown when custom is selected) */}
            {period === 'custom' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground shrink-0">〜</span>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="flex-1"
                />
              </div>
            )}

            {/* Step 2: Entry count preview — KPI mini style */}
            {period && (
              <div className="rounded-xl border bg-muted/30 p-4">
                {counting ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    エントリを検索中...
                  </div>
                ) : entryCount !== null ? (
                  entryCount > 0 ? (
                    <div className="flex items-center gap-3">
                      <div className="rounded-full p-1.5 bg-blue-500/10">
                        <FileText className="h-4 w-4 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold leading-tight">{entryCount}<span className="text-xs text-muted-foreground ml-0.5">件</span></p>
                        <p className="text-xs text-muted-foreground">の日記がヒットしました</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Search className="h-4 w-4" />
                      <span>この期間にエントリがありません</span>
                    </div>
                  )
                ) : period === 'custom' && (!customFrom || !customTo) ? (
                  <p className="text-sm text-muted-foreground">開始日と終了日を選択してください</p>
                ) : null}
              </div>
            )}

            {/* Step 3: Generate button */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Button
                onClick={handleGenerate}
                disabled={!canGenerate}
                size="lg"
                className="w-full gap-2"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    生成中...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-5 w-5" />
                    振り返りを生成する
                  </>
                )}
              </Button>
            </motion.div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </CardContent>
        </Card>
      )}

      {/* Current review result */}
      {displayReview && (
        <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>}>
          <ReviewAlbum review={displayReview} />
        </Suspense>
      )}

      {/* Past reviews */}
      {pastReviews.length > 0 && (
        <Card className="rounded-xl shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">過去の振り返り</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pastReviews.map(r => (
              <div key={r.id} className="relative overflow-hidden rounded-xl group">
                {/* 背景の削除ボタン (swipe) */}
                <div className="absolute inset-y-0 right-0 flex items-center bg-destructive px-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(r.id); }}
                    className="text-destructive-foreground text-sm font-medium whitespace-nowrap"
                  >
                    削除
                  </button>
                </div>
                {/* 前面のコンテンツ */}
                <button
                  className={`relative w-full flex items-center justify-between p-3 bg-card rounded-xl text-left transition-transform duration-200 ${
                    swipedId === r.id ? '-translate-x-20' : 'translate-x-0'
                  }`}
                  onClick={(e) => {
                    if (swipedId === r.id) {
                      e.stopPropagation();
                      setSwipedId(null);
                    } else {
                      handleViewPast(r.id);
                    }
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove(r.id)}
                >
                  <div>
                    <p className="text-sm font-medium">{periodLabels[r.period as Period] || r.period}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.fromDate && r.toDate
                        ? `${format(parseISO(r.fromDate), 'M/d', { locale: ja })} 〜 ${format(parseISO(r.toDate), 'M/d', { locale: ja })}`
                        : ''}
                      {r.generatedAt && ` (${format(parseISO(r.generatedAt), 'M/d HH:mm')})`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Desktop hover delete button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(r.id); }}
                      className="hidden sm:flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {loadingPast && (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>振り返りを削除</DialogTitle>
            <DialogDescription>
              この振り返りを削除しますか？この操作は取り消せません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              キャンセル
            </Button>
            <Button variant="destructive" onClick={handleDeleteReview} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PremiumModal
        open={showPremiumModal}
        onOpenChange={setShowPremiumModal}
        onActivated={() => refreshSubscription()}
      />
    </div>
  );
}
